import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { Icon } from "antd";
import DEATH_API from "../../apis/death.api";
import { useTheme } from "../../utils/ThemeContext";
import "./new-user-onboarding-widget.css";

export const NewUserOnboardingWidget = ({ onClose }) => {
  const { theme } = useTheme();
  const history = useHistory();
  const [completedSteps, setCompletedSteps] = useState({});

  const steps = [
    {
      id: 1,
      title: "Connect Your Financial Accounts",
      desc: "Link your bank accounts for real-time views.",
      route: "/plaid",
      action: "link_bank",
    },
    {
      id: 2,
      title: "Import Your Contacts",
      desc: "Centralize your financial network.",
      route: "/client/plan",
    },
    {
      id: 3,
      title: "Set Up Your Income Sources",
      desc: "Document employment and investment income.",
      route: "/income",
    },
    {
      id: 4,
      title: "Add Assets Manually",
      desc: "Add assets that don't import automatically.",
      route: "/assets",
    },
    {
      id: 5,
      title: "Add Liabilities Manually",
      desc: "Include debts not in linked accounts.",
      route: "/liabilities_credit",
    },
    {
      id: 6,
      title: "Customize Your Dashboard",
      desc: "Arrange tiles to suit your needs.",
      route: "/dashboard",
      action: "customize_dashboard",
    },
    {
      id: 7,
      title: "Set Your Financial Goals",
      desc: "Establish objectives like retirement or savings.",
      route: "/goals",
    },
  ];

  useEffect(() => {
    const settingRaw = localStorage.getItem("setting");
    if (settingRaw && settingRaw !== "undefined") {
      const settings = JSON.parse(settingRaw);
      if (settings.onboardingSteps) {
        setCompletedSteps(settings.onboardingSteps);
      }
    }
    getUserRole();
  }, []);

  const getUserRole = async () => {
    try {
      let data = await DEATH_API.getUpdatedRole();
      if (data && data.data && data.data.setting) {
        localStorage.setItem("setting", JSON.stringify(data.data.setting));
        if (data.data.setting.onboardingSteps) {
          setCompletedSteps(data.data.setting.onboardingSteps);
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleCheckClick = async (e, stepName) => {
    e.stopPropagation();

    // 1. Get current settings from localStorage to ensure we have the latest data
    const settingRaw = localStorage.getItem("setting");
    let userSettings =
      settingRaw && settingRaw !== "undefined" ? JSON.parse(settingRaw) : {};

    // 2. Ensure onboardingSteps is an object
    if (
      !userSettings.onboardingSteps ||
      Array.isArray(userSettings.onboardingSteps)
    ) {
      userSettings.onboardingSteps = {};
    }

    // 3. Calculate new state for the step
    const updatedOnboardingSteps = { ...userSettings.onboardingSteps };
    if (updatedOnboardingSteps[stepName]) {
      delete updatedOnboardingSteps[stepName];
    } else {
      updatedOnboardingSteps[stepName] = true;
    }

    // 4. Optimistic Update: Update React state and localStorage immediately
    setCompletedSteps(updatedOnboardingSteps);
    userSettings.onboardingSteps = updatedOnboardingSteps;
    localStorage.setItem("setting", JSON.stringify(userSettings));

    try {
      // 5. Persist to server
      await DEATH_API.updateUserAssetSetting(userSettings);
    } catch (error) {
      console.error("Error updating settings:", error);
    }
  };

  const handleStepClick = (step) => {
    if (step.route) {
      history.push(step.route);
    }
  };

  const completedCount = Object.keys(completedSteps).length;
  const progress = Math.round((completedCount / steps.length) * 100);

  return (
    <div
      className={`new-user-onboarding-widget ${
        theme === "dark" ? "dark-mode" : ""
      }`}
    >
      <div className="widget-header">
        <div className="widget-title">Getting Started with Asset Planet</div>
        <button className="dismiss-btn" onClick={onClose} title="Hide Widget">
          <Icon type="close" />
        </button>
      </div>

      <div className="progress-container">
        <div className="progress-bar-bg">
          <div
            className="progress-bar-fill"
            style={{ width: `${progress}%` }}
          ></div>
        </div>
        <div className="progress-text">{progress}% Complete</div>
      </div>

      <div className="steps-grid">
        {steps.map((step) => (
          <div
            key={step.id}
            className="step-card"
            onClick={() => handleStepClick(step)}
          >
            <div className="step-header">
              <div className="step-title">
                {step.id}. {step.title}
              </div>
              <div
                className={`step-check ${
                  completedSteps[step.title] ? "completed" : ""
                }`}
                onClick={(e) => handleCheckClick(e, step.title)}
              >
                {completedSteps[step.title] && <Icon type="check" />}
              </div>
            </div>
            <div className="step-desc">{step.desc}</div>
          </div>
        ))}
      </div>
    </div>
  );
};
